import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { adminClient } from "services/apiClient";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";

const SUBS_CACHE_KEY = 'SubscriptionsList';
const PLANS_CACHE_KEY = 'SubscriptionPlansData';

function SubscriptionManagement() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planPrice, setPlanPrice] = useState("");

  const [filters, setFilters] = useState({ query: "", status: 'all' });
  const [processing, setProcessing] = useState(false);

  const [rows, setRows] = useState([]);
  const [confirmRenewal, setConfirmRenewal] = useState(null); // row
  const [confirmDeactivate, setConfirmDeactivate] = useState(null); // row
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [confirmActivate, setConfirmActivate] = useState(null); // row
  const [assignRow, setAssignRow] = useState(null); // row for "Abonelik Tanımla"
  const [assignForm, setAssignForm] = useState({
    purchasedDate: "",
    endDate: "",
    price: "",
    billingCycle: "yearly",
    durationMonths: 0,
    durationYears: 1,
    hasMultilanguage: false,
    purchaseMethod: "invoice",
  });

  // Helpers for datetime-local formatting and quick date ops
  const pad2 = (n) => String(n).padStart(2, '0');
  const toLocalInputValue = (date) => {
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = pad2(d.getMonth() + 1);
    const dd = pad2(d.getDate());
    const hh = pad2(d.getHours());
    const mi = pad2(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };
  const todayAt2359 = () => {
    const d = new Date();
    d.setHours(23, 59, 0, 0);
    return d;
  };
  const addMonths = (base, months) => {
    const d = new Date(base);
    const day = d.getDate();
    d.setMonth(d.getMonth() + months);
    // handle month overflow (e.g., Jan 31 + 1 month)
    if (d.getDate() < day) d.setDate(0);
    return d;
  };
  const addYears = (base, years) => {
    const d = new Date(base);
    d.setFullYear(d.getFullYear() + years);
    return d;
  };

  const seedFromCache = () => {
    try {
      const raw = sessionStorage.getItem(SUBS_CACHE_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return false;
      setSubscriptions(parsed);
      setRows(parsed);
      applyLocalFilter(parsed, filters);
      return true;
    } catch {
      return false;
    }
  };

  const applyLocalFilter = (base = subscriptions, f = filters) => {
    let list = Array.isArray(base) ? base : [];
    const q = (f.query || '').toLowerCase().trim();
    if (q) {
      list = list.filter(x =>
        (x.clientName || '').toLowerCase().includes(q) ||
        (x.domain || '').toLowerCase().includes(q)
      );
    }
    if (f.status === 'active') list = list.filter(x => x.isActive === true);
    if (f.status === 'passive') list = list.filter(x => x.isActive === false);
    setRows(list);
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "-");
  const formatTL = (n) => (n == null ? "-" : `₺${Number(n).toLocaleString("tr-TR")}`);

  const openMenu = (event, row) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 75, sortable: true },
    { field: 'clientName', headerName: 'Müşteri', flex: 1, minWidth: 160, sortable: true, valueGetter: (p) => p.row.clientName || '-' },
    { field: 'domain', headerName: 'Domain', flex: 1, minWidth: 120, sortable: true, valueGetter: (p) => p.row.domain || '-' },
    { field: 'endDate', headerName: 'Bitiş Tarihi', width: 140, sortable: true, renderCell: (p) => {
      const isExpired = p.row.endDate && new Date(p.row.endDate) < new Date();
      return (
        <Typography variant="body2" color={isExpired ? 'error' : 'inherit'}>
          {formatDate(p.row.endDate)}
        </Typography>
      );
    } },
    { field: 'price', headerName: 'Fiyat', width: 140, sortable: true, renderCell: (p) => formatTL(p.row.price) },
    { field: 'billingCycle', headerName: 'Periyot', width: 120, sortable: true },
    {
      field: 'isActive',
      headerName: 'Durum',
      width: 110,
      sortable: true,
      renderCell: (p) => (
        <Box sx={{
          px: 1,
          py: 0.25,
          borderRadius: 1,
          bgcolor: p.value ? '#2e7d32' : '#9e9e9e',
          color: p.value ? '#fff' : '#000',
          fontSize: '0.75rem',
          fontWeight: 700,
          textTransform: 'uppercase'
        }}>
          {p.value ? 'Aktif' : 'Pasif'}
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Seç',
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => openMenu(e, params.row)}
          onMouseDown={(e) => e.stopPropagation()}
          tabIndex={-1}
          aria-label="işlemler"
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  const fetchSubscriptions = async (options = {}) => {
    setLoading(true);
    setError("");
    try {
      const response = await adminClient.get("/admin/active-subscriptions?status=all", {
        params: options,
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setSubscriptions(list);
      setRows(list);
      sessionStorage.setItem(SUBS_CACHE_KEY, JSON.stringify(list));
      // re-apply current UI filters on fresh data
      applyLocalFilter(list, filters);
    } catch (err) {
      setError(err.response?.data?.message || "Abonelikler alınamadı.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    setLoadingPlans(true);
    try {
      const response = await adminClient.get("/admin/subscription-plans");
      const list = Array.isArray(response.data) ? response.data : [];
      setPlans(list);
      sessionStorage.setItem(PLANS_CACHE_KEY, JSON.stringify(list));
    } catch (err) {
      setError(err.response?.data?.error || "Planlar alınamadı.");
    } finally {
      setLoadingPlans(false);
    }
  };

  useEffect(() => {
    const hadCache = seedFromCache();
    if (!hadCache) {
      fetchSubscriptions();
    }
    fetchPlans();
  }, []);

  const savePlanPrice = async () => {
    const numericPrice = Number(planPrice);
    if (!editingPlan || !planPrice.trim() || !Number.isFinite(numericPrice) || numericPrice < 0) {
      setError("Geçerli bir fiyat giriniz.");
      return;
    }

    setProcessing(true);
    setError("");
    setSuccess("");
    try {
      const response = await adminClient.patch(
        `/admin/subscription-plans/${editingPlan.id}/price`,
        { price: numericPrice }
      );
      const deployment = response.data?.websiteDeployment;

      if (deployment?.status === 'unchanged') {
        setSuccess("Fiyat değişmedi; website yayını tetiklenmedi.");
      } else if (deployment?.required && !deployment?.triggered) {
        setError(
          "Fiyat kaydedildi ancak website yayını tetiklenemedi. Vercel Deploy Hook ayarını kontrol edin."
        );
      } else if (deployment?.triggered) {
        setSuccess("Fiyat kaydedildi ve website yayını tetiklendi.");
      } else {
        setSuccess("Fiyat kaydedildi. Bu plan website yayını gerektirmiyor.");
      }

      setEditingPlan(null);
      setPlanPrice("");
      sessionStorage.removeItem(PLANS_CACHE_KEY);
      await fetchPlans();
    } catch (err) {
      setError(err.response?.data?.error || "Plan fiyatı güncellenemedi.");
    } finally {
      setProcessing(false);
    }
  };

  const handleFilter = () => applyLocalFilter(subscriptions, filters);

  return (
    <DashboardLayout>
      <Stack spacing={3} mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Abonelik Yönetimi
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Aktif abonelikleri görüntüleyin, manuel lisans yenileme ve durdurma işlemlerini yönetin.
        </Typography>
      </Stack>

      {error && (
        <Box mb={3}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      {success && (
        <Box mb={3}>
          <Alert severity="success">{success}</Alert>
        </Box>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ md: 'center' }}
            justifyContent="space-between"
            mb={2}
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>Plan Fiyatları</Typography>
              <Typography variant="body2" color="text.secondary">
                Tüm plan fiyatlarını düzenleyin. Plan ID 4 güncellendiğinde website yayını otomatik tetiklenir.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchPlans}
              disabled={loadingPlans}
              sx={{ color: '#000', flexShrink: 0 }}
            >
              Planları Yenile
            </Button>
          </Stack>

          {loadingPlans ? (
            <Stack alignItems="center" py={3}><CircularProgress size={28} /></Stack>
          ) : (
            <Grid container spacing={2}>
              {plans.map((plan) => (
                <Grid item xs={12} sm={6} lg={4} key={plan.id}>
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Box minWidth={0}>
                      <Typography variant="subtitle2" noWrap>{plan.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID {plan.id} · {plan.billingCycle || '-'}
                      </Typography>
                      <Typography variant="h6" mt={0.5}>{formatTL(plan.price)}</Typography>
                    </Box>
                    <IconButton
                      aria-label={`${plan.name} fiyatını düzenle`}
                      onClick={() => {
                        setEditingPlan(plan);
                        setPlanPrice(plan.price == null ? "" : String(plan.price));
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(editingPlan)} onClose={() => setEditingPlan(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Plan Fiyatını Düzenle</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Typography variant="body2">
              {editingPlan?.name} · ID {editingPlan?.id}
            </Typography>
            <TextField
              autoFocus
              label="Fiyat (TL)"
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              value={planPrice}
              onChange={(event) => setPlanPrice(event.target.value)}
              fullWidth
            />
            {editingPlan?.id === 4 && (
              <Alert severity="info">
                Bu fiyat kaydedildiğinde logicookie.com için yeni Vercel yayını tetiklenir.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingPlan(null)} disabled={processing}>Vazgeç</Button>
          <Button variant="contained" onClick={savePlanPrice} disabled={processing} sx={{ color: '#fff' }}>
            {processing ? 'Kaydediliyor…' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2} alignItems={{ md: 'center' }} justifyContent="space-between">
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexGrow={1} justifyContent="space-between" alignItems="center">
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                      label="Müşteri / Domain ara"
                      value={filters.query}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFilters((p) => { const n = { ...p, query: v }; applyLocalFilter(subscriptions, n); return n; });
                      }}
                      size="small"
                      sx={{
                        minWidth: { xs: '100%', md: 300 },
                        maxWidth: 380,
                        flexShrink: 0,
                        '& .MuiInputBase-root': { height: 44.14 },
                        '& .MuiInputBase-input': { height: 44.14, boxSizing: 'border-box', padding: '10px 14px' }
                      }}
                    />
                    <FormControl sx={{ minWidth: { xs: '100%', md: 160 }, height: 44.14 }}>
                      <InputLabel id="status-filter-label">Durum</InputLabel>
                      <Select
                        labelId="status-filter-label"
                        label="Durum"
                        value={filters.status}
                        onChange={(e) => {
                          const v = e.target.value;
                          setFilters((p) => { const n = { ...p, status: v }; applyLocalFilter(subscriptions, n); return n; });
                        }}
                        sx={{ height: 44.14, display: 'flex', alignItems: 'center'}}
                      >
                        <MenuItem value="all">Tümü</MenuItem>
                        <MenuItem value="active">Aktif</MenuItem>
                        <MenuItem value="passive">Pasif</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => fetchSubscriptions()}
                    sx={{ color: '#000', height: 44.13 }}
                  >
                    Yenile
                  </Button>
                </Stack>
              </Stack>

              {loading ? (
                <Stack alignItems="center" py={6}><CircularProgress size={32} /></Stack>
              ) : (
                <Box sx={{ height: 560, width: '100%' }}>
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.id}
                    disableSelectionOnClick
                    sortingOrder={["desc", "asc"]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 25, page: 0 } },
                      sorting: { sortModel: [{ field: 'id', sort: 'desc' }] },
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    sx={{
                      '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                      '& .MuiDataGrid-row:hover': { backgroundColor: '#f5f5f5' }
                    }}
                  />
                </Box>
              )}
            </CardContent>

            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={closeMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  closeMenu();
                  setAssignRow(menuRow);
                  setAssignForm({
                    purchasedDate: "",
                    endDate: "",
                    price: "",
                    billingCycle: "yearly",
                    durationMonths: 0,
                    durationYears: 1,
                    hasMultilanguage: false,
                    purchaseMethod: "invoice",
                  });
                }}
              >
                Abonelik Tanımla
              </MenuItem>
              <MenuItem
                onClick={(e) => { e.stopPropagation(); closeMenu(); setConfirmRenewal(menuRow); }}
              >
                Lisansı Yenile
              </MenuItem>
              {menuRow?.isActive ? (
                <MenuItem
                  onClick={(e) => { e.stopPropagation(); closeMenu(); setConfirmDeactivate(menuRow); }}
                >
                  Pasifleştir
                </MenuItem>
              ) : (
                <MenuItem
                  onClick={(e) => { e.stopPropagation(); closeMenu(); setConfirmActivate(menuRow); }}
                >
                  Aktifleştir
                </MenuItem>
              )}
            </Menu>

            <Dialog open={Boolean(confirmRenewal)} onClose={() => setConfirmRenewal(null)} maxWidth="xs" fullWidth>
              <DialogTitle>Lisans Yenile</DialogTitle>
              <DialogContent>Bu aboneliği yenilemek istediğinize emin misiniz?</DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirmRenewal(null)}>Vazgeç</Button>
                <Button
                  variant="contained"
                  onClick={async () => {
                    if (!confirmRenewal) return;
                    setProcessing(true);
                    setError(''); setSuccess('');
                    try {
                      await adminClient.post('/admin/subscriptions/manual-renewal', { subscriptionId: Number(confirmRenewal.id) });
                      setSuccess('Abonelik yenilendi.');
                      setConfirmRenewal(null);
                      handleFilter();
                    } catch (err) {
                      setError(err.response?.data?.message || 'Yenileme işlemi başarısız oldu.');
                    } finally {
                      setProcessing(false);
                    }
                  }}
                  sx={{ color: '#fff' }}
                >
                  {processing ? 'İşlem yapılıyor…' : 'Yenile'}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={Boolean(confirmDeactivate)} onClose={() => setConfirmDeactivate(null)} maxWidth="xs" fullWidth>
              <DialogTitle>Aboneliği Pasifleştir</DialogTitle>
              <DialogContent>Bu aboneliği pasifleştirmek istediğinize emin misiniz?</DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirmDeactivate(null)}>Vazgeç</Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#fff',
                    color: 'red',
                    '&:hover': { backgroundColor: 'red', color: '#fff' }
                  }}
                  onClick={async () => {
                    if (!confirmDeactivate) return;
                    setProcessing(true);
                    setError(''); setSuccess('');
                    try {
                      await adminClient.put('/admin/subscriptions/deactivate', { subscriptionId: Number(confirmDeactivate.id) });
                      setSuccess('Abonelik pasif hale getirildi.');
                      setConfirmDeactivate(null);
                      handleFilter();
                    } catch (err) {
                      setError(err.response?.data?.message || 'Abonelik pasifleştirilemedi.');
                    } finally {
                      setProcessing(false);
                    }
                  }}
                >
                  {processing ? 'İşlem yapılıyor…' : 'Pasifleştir'}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={Boolean(confirmActivate)} onClose={() => setConfirmActivate(null)} maxWidth="xs" fullWidth>
              <DialogTitle>Aboneliği Aktifleştir</DialogTitle>
              <DialogContent>Bu aboneliği aktifleştirmek istediğinize emin misiniz?</DialogContent>
              <DialogActions>
                <Button onClick={() => setConfirmActivate(null)}>Vazgeç</Button>
                <Button
                  variant="contained"
                  onClick={async () => {
                    if (!confirmActivate) return;
                    setProcessing(true);
                    setError(''); setSuccess('');
                    try {
                      await adminClient.post('/admin/subscriptions/reactivate', { subscriptionId: Number(confirmActivate.id) });
                      setSuccess('Abonelik aktif hale getirildi.');
                      setConfirmActivate(null);
                      handleFilter();
                    } catch (err) {
                      setError(err.response?.data?.message || 'Abonelik aktifleştirilemedi.');
                    } finally {
                      setProcessing(false);
                    }
                  }}
                  sx={{ color: '#fff' }}
                >
                  {processing ? 'İşlem yapılıyor…' : 'Aktifleştir'}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={Boolean(assignRow)} onClose={() => setAssignRow(null)} maxWidth="sm" fullWidth>
              <DialogTitle>Abonelik Tanımla</DialogTitle>
              <DialogContent>
                <Stack spacing={2} mt={1}>
                  {/* Satın alma tarihi & Bitiş tarihi yan yana */}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Stack flex={1} spacing={1}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="caption" sx={{ opacity: 0.8, mb: 0.5 }}>Satın Alma Tarihi</Typography>
                        <Button
                          size="small"
                          onClick={() => {
                            const d = todayAt2359();
                            setAssignForm((p) => ({ ...p, purchasedDate: toLocalInputValue(d) }));
                          }}
                        >
                          Bugün
                        </Button>
                      </Stack>
                      <TextField
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                        value={assignForm.purchasedDate}
                        onChange={(e) => setAssignForm((p) => ({ ...p, purchasedDate: e.target.value }))}
                        fullWidth
                      />
                    </Stack>

                    <Stack flex={1} spacing={1}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="caption" sx={{ opacity: 0.8, mb: 0.5 }}>Bitiş Tarihi</Typography>
                        <Stack direction="row" spacing={0.5}>
                          <Button
                            size="small"
                            sx={{ minWidth: 0, px: 1 }}
                            onClick={() => {
                              const base = assignForm.purchasedDate ? new Date(assignForm.purchasedDate) : new Date();
                              const d = addMonths(base, 1);
                              setAssignForm((p) => ({ ...p, endDate: toLocalInputValue(d) }));
                            }}
                          >
                            1 Ay
                          </Button>
                          <Button
                            size="small"
                            sx={{ minWidth: 0, px: 1 }}
                            onClick={() => {
                              const base = assignForm.purchasedDate ? new Date(assignForm.purchasedDate) : new Date();
                              const d = addMonths(base, 6);
                              setAssignForm((p) => ({ ...p, endDate: toLocalInputValue(d) }));
                            }}
                          >
                            6 Ay
                          </Button>
                          <Button
                            size="small"
                            sx={{ minWidth: 0, px: 1 }}
                            onClick={() => {
                              const base = assignForm.purchasedDate ? new Date(assignForm.purchasedDate) : new Date();
                              const d = addYears(base, 1);
                              setAssignForm((p) => ({ ...p, endDate: toLocalInputValue(d) }));
                            }}
                          >
                            1 Yıl
                          </Button>
                          <Button
                            size="small"
                            sx={{ minWidth: 0, px: 1 }}
                            onClick={() => {
                              const base = assignForm.purchasedDate ? new Date(assignForm.purchasedDate) : new Date();
                              const d = addYears(base, 2);
                              setAssignForm((p) => ({ ...p, endDate: toLocalInputValue(d) }));
                            }}
                          >
                            2 Yıl
                          </Button>
                        </Stack>
                      </Stack>
                      <TextField
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                        value={assignForm.endDate}
                        onChange={(e) => setAssignForm((p) => ({ ...p, endDate: e.target.value }))}
                        fullWidth
                      />
                    </Stack>
                  </Stack>

                  {/* Fiyat & Periyot yan yana */}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Fiyat (₺)"
                      type="number"
                      value={assignForm.price}
                      onChange={(e) => setAssignForm((p) => ({ ...p, price: e.target.value }))}
                      sx={{ flex: 1 }}
                    />
                    <FormControl sx={{ flex: 1, height: 44.14 }}>
                      <InputLabel id="billing-cycle-label">Periyot</InputLabel>
                      <Select
                        labelId="billing-cycle-label"
                        label="Periyot"
                        value={assignForm.billingCycle}
                        onChange={(e) => setAssignForm((p) => ({ ...p, billingCycle: e.target.value }))}
                        sx={{ height: 44.14, display: 'flex', alignItems: 'center' }}
                      >
                        <MenuItem value="monthly">Aylık</MenuItem>
                        <MenuItem value="yearly">Yıllık</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>

                  {/* Süre (Ay & Yıl) yan yana */}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Süre (Ay)"
                      type="number"
                      value={assignForm.durationMonths}
                      onChange={(e) => setAssignForm((p) => ({ ...p, durationMonths: Number(e.target.value) }))}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Süre (Yıl)"
                      type="number"
                      value={assignForm.durationYears}
                      onChange={(e) => setAssignForm((p) => ({ ...p, durationYears: Number(e.target.value) }))}
                      sx={{ flex: 1 }}
                    />
                  </Stack>

                  {/* Satın alma yöntemi & Çok dilli yan yana */}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl sx={{ flex: 1, height: 44.14 }}>
                      <InputLabel id="purchase-method-label">Satın Alma Yöntemi</InputLabel>
                      <Select
                        labelId="purchase-method-label"
                        label="Satın Alma Yöntemi"
                        value={assignForm.purchaseMethod}
                        onChange={(e) => setAssignForm((p) => ({ ...p, purchaseMethod: e.target.value }))}
                        sx={{ height: 44.14, display: 'flex', alignItems: 'center' }}
                      >
                        <MenuItem value="invoice">Fatura</MenuItem>
                        <MenuItem value="card">Kart</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1, height: 44.14 }}>
                      <InputLabel id="has-multilang-label">Çok Dilli</InputLabel>
                      <Select
                        labelId="has-multilang-label"
                        label="Çok Dilli"
                        value={assignForm.hasMultilanguage ? "true" : "false"}
                        onChange={(e) => setAssignForm((p) => ({ ...p, hasMultilanguage: e.target.value === "true" }))}
                        sx={{ height: 44.14, display: 'flex', alignItems: 'center' }}
                      >
                        <MenuItem value="false">Hayır</MenuItem>
                        <MenuItem value="true">Evet</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setAssignRow(null)}>Vazgeç</Button>
                <Button
                  variant="contained"
                  sx={{ color: '#fff' }}
                  onClick={async () => {
                    if (!assignRow) return;
                    setProcessing(true);
                    setError(''); setSuccess('');
                    try {
                      const body = {
                        subscriptionId: Number(assignRow.id),
                        purchasedDate: assignForm.purchasedDate ? new Date(assignForm.purchasedDate).toISOString() : undefined,
                        endDate: assignForm.endDate ? new Date(assignForm.endDate).toISOString() : undefined,
                        price: assignForm.price !== "" ? Number(assignForm.price) : undefined,
                        billingCycle: assignForm.billingCycle,
                        durationMonths: Number(assignForm.durationMonths) || 0,
                        durationYears: Number(assignForm.durationYears) || 0,
                        hasMultilanguage: Boolean(assignForm.hasMultilanguage),
                        purchaseMethod: ['invoice', 'card'].includes(assignForm.purchaseMethod) ? assignForm.purchaseMethod : 'invoice',
                      };
                      await adminClient.post('/admin/subscriptions/assign', body);
                      setSuccess('Abonelik tanımlandı.');
                      setAssignRow(null);
                      handleFilter();
                    } catch (err) {
                      setError(err.response?.data?.message || 'Abonelik tanımlanamadı.');
                    } finally {
                      setProcessing(false);
                    }
                  }}
                >
                  {processing ? 'İşlem yapılıyor…' : 'Kaydet'}
                </Button>
              </DialogActions>
            </Dialog>
          </Card>
        </Grid>
      </Grid>

      <Footer />
    </DashboardLayout>
  );
}

export default SubscriptionManagement;
